import type { Layout } from '../../data/types';
import type { AABB, PolygonWithHoles } from '../../geo/types';
import type { GroundPiece } from '../../site/types';
import { difference, fromRing, intersection } from '../../geo/clip';
import { ringBounds } from '../../geo/polygon';
import { polylineLength } from '../../geo/polyline';
import { SOURCE_PRIORITY } from '../fabric/lotRules';
import { cropToBox } from './boxCrop';
import { streetFrame, type StreetFrame } from './streetFrame';

type Street = Layout['streets'][number];
type PavedKind = 'street' | 'alley' | 'quay';

export type PavedStreet = Street & { readonly kind: PavedKind };

/** Street ground one street paves, within one ground piece. */
export interface Part {
  readonly ground: GroundPiece;
  readonly polygon: PolygonWithHoles;
  readonly box: AABB;
}

interface Band {
  readonly polygons: readonly PolygonWithHoles[];
  readonly box: AABB;
}

export interface FramedStreet {
  readonly street: PavedStreet;
  readonly frame: StreetFrame;
  /** The ground the street owns between its ends. */
  readonly body: Band;
  /** The ground it owns past each end. */
  readonly ends: readonly Band[];
}

// A street owns ground this far past its half-width, which covers the 0.1 m the partition's
// simplification moves the street ground's outline (partition.ts SIMPLIFY_M).
const OWN_MARGIN_M = 0.2;
// A street's frame reaches this far past its half-width, beyond the ground the street owns, so the
// claims on it cover all of that ground.
const FRAME_MARGIN_M = 0.5;
// The order the lots claim their ground in (fabricLots.ts): a quay first, an alley last.
const PRIORITY: Readonly<Record<PavedKind, number>> = {
  quay: SOURCE_PRIORITY.quay,
  street: SOURCE_PRIORITY.street,
  alley: SOURCE_PRIORITY.alley,
};

function isPaved(street: Street): street is PavedStreet {
  return street.kind in PRIORITY && polylineLength(street.centerline) > 0;
}

function boundsOf(boxes: readonly AABB[]): AABB {
  return {
    minX: Math.min(...boxes.map((b) => b.minX)),
    minY: Math.min(...boxes.map((b) => b.minY)),
    maxX: Math.max(...boxes.map((b) => b.maxX)),
    maxY: Math.max(...boxes.map((b) => b.maxY)),
  };
}

export function boxesMeet(a: AABB, b: AABB): boolean {
  return a.minX <= b.maxX && b.minX <= a.maxX && a.minY <= b.maxY && b.minY <= a.maxY;
}

function bandOf(frame: StreetFrame, from: number, to: number, reach: number): Band {
  const ring = frame.outline([{ from, to, bottom: -reach, top: reach }]);
  return { polygons: [fromRing(ring)], box: ringBounds(ring) };
}

/* The ground a street owns: its frame within OWN_MARGIN_M of its half-width, mitred at its bends
   and square past its ends, so it holds the rounded buffer the street ground was made from. */
function framed(street: PavedStreet): FramedStreet {
  const frame = streetFrame(street.centerline, street.width / 2 + FRAME_MARGIN_M);
  const reach = street.width / 2 + OWN_MARGIN_M;
  return {
    street,
    frame,
    body: bandOf(frame, 0, frame.length, reach),
    ends: [
      bandOf(frame, -reach, 0, reach),
      bandOf(frame, frame.length, frame.length + reach, reach),
    ],
  };
}

/** The paved streets in the order they take their ground: the lots' order, then the layout's. */
export function framedStreets(streets: Layout['streets']): readonly FramedStreet[] {
  return streets
    .filter(isPaved)
    .sort((a, b) => PRIORITY[a.kind] - PRIORITY[b.kind])
    .map(framed);
}

interface NearGround {
  readonly piece: GroundPiece;
  readonly polygon: PolygonWithHoles;
}

/* Cuts are cropped to the band as well: a crop can move an outline it cuts short by Clipper's
   millimetre, which the pieces' conforming takes up. */
function partsIn(near: readonly NearGround[], band: Band, cuts: readonly Band[]): Part[] {
  const cut = cuts
    .filter((other) => boxesMeet(other.box, band.box))
    .flatMap((other) => other.polygons.flatMap((p) => cropToBox(p, band.box) ?? []));
  return near.flatMap(({ piece, polygon }) => {
    const inBand = intersection([polygon], band.polygons);
    const owned = cut.length > 0 && inBand.length > 0 ? difference(inBand, cut) : inBand;
    return owned.map((p) => ({ ground: piece, polygon: p, box: ringBounds(p.outer) }));
  });
}

function nearGround(
  ground: readonly { readonly piece: GroundPiece; readonly box: AABB }[],
  box: AABB
): NearGround[] {
  return ground
    .filter((g) => boxesMeet(g.box, box))
    .flatMap(({ piece }) => {
      const polygon = cropToBox(piece.polygon, box);
      return polygon === null ? [] : [{ piece, polygon }];
    });
}

/**
 * The street ground each street paves. Streets take their ground
 * between their ends first, in order, and then past their ends: a street that ends in another
 * takes none of the ground the other runs through.
 */
export function ownedGround(
  streets: readonly FramedStreet[],
  ground: readonly GroundPiece[]
): readonly (readonly Part[])[] {
  const streetGround = ground
    .filter((piece) => piece.kind === 'street')
    .map((piece) => ({ piece, box: ringBounds(piece.polygon.outer) }));
  const bodies = streets.map((paved) => paved.body);
  return streets.map((paved, k) => {
    const near = nearGround(
      streetGround,
      boundsOf([paved.body.box, ...paved.ends.map((e) => e.box)])
    );
    const earlierEnds = streets.slice(0, k).flatMap((earlier) => earlier.ends);
    return [
      ...partsIn(near, paved.body, bodies.slice(0, k)),
      ...paved.ends.flatMap((end) => partsIn(near, end, [...bodies, ...earlierEnds])),
    ];
  });
}
